import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProjectFormStore } from "@/store/project-form-provider";
import { useProjectResults } from "@/utils/use-project-results";
import { formatNumber, formatPercent } from "@/utils/format";

type AnyDict = Record<string, any>;
type CellFmt = "number" | "percent";

type RowDef = {
  section: string;
  key: string;
  label: string;
  unit?: string;
  fmt?: CellFmt;
  bold?: boolean;
  decimals?: number;   // formatting override for numbers
  noTotal?: boolean;   // suppress totals column
};

export default function ProjectFinancialModel() {
  const { id } = useParams();
  const { results, loading, error } = useProjectResults(id);
  const { project } = useProjectFormStore();
  const p: any = project;

  const { t } = useTranslation();

  const SECTION_LABELS: Record<string, string> = {
    "Production": t("project.financial_model.sections.production"),
    "Sales": t("project.financial_model.sections.sales"),
    "OpEx": t("project.financial_model.sections.opex"),
    "CapEx": t("project.financial_model.sections.capex"),
    "Financing": t("project.financial_model.sections.financing"),
    "Depreciation": t("project.financial_model.sections.depreciation"),
    "Tax": t("project.financial_model.sections.tax"),
    "Cash flow statement": t("project.financial_model.sections.cash_flow_statement"),
  };

  const arr = (x: any): AnyDict[] | undefined => (Array.isArray(x) ? (x as AnyDict[]) : undefined);
  const get = (a: AnyDict[] | undefined, y: number, key: string): number | undefined => {
    if (!a) return undefined;
    const row = a.find((r) => Number(r?.Year) === y);
    const v = row?.[key];
    return typeof v === "number" ? v : undefined;
  };

  // series from backend
  const prod     = arr((results as any)?.production);
  const tariff   = arr((results as any)?.tariff);
  const rev      = arr((results as any)?.revenue);
  const inv      = arr((results as any)?.investment);
  const dep      = arr((results as any)?.depreciation);
  const cashflow = arr((results as any)?.cashflow);

  const fin_interest       = arr((results as any)?.interest);
  const fin_redemption     = arr((results as any)?.redemption);
  const fin_debt_service   = arr((results as any)?.debt_service);
  const fin_structuring    = arr((results as any)?.structuring_costs);
  const fin_remaining_debt = arr((results as any)?.remaining_debt);
  const fin_debt           = arr((results as any)?.debt);
  const fin_net_debt       = arr((results as any)?.net_debt);

  const opex_total      = arr((results as any)?.opex);
  const opex_items_sets = (results as any)?.opex_items as AnyDict[][] | undefined;

  // unified years (+ ensure Year 0 present as leftmost column)
  const years: number[] = useMemo(() => {
    const ys: number[] = [];
    const push = (a?: AnyDict[]) => a?.forEach((r) => typeof r?.Year === "number" && ys.push(r.Year));
    [prod, tariff, rev, inv, dep, fin_interest, fin_redemption, fin_structuring, fin_debt_service, fin_remaining_debt, fin_debt, fin_net_debt, opex_total, cashflow].forEach(push);
    opex_items_sets?.forEach(push);
    let uniq = Array.from(new Set(ys)).sort((a, b) => a - b);
    if (!uniq.includes(0)) uniq = [0, ...uniq];
    return uniq;
  }, [results]);

  // discover opex item names dynamically
  const opexItemNames: string[] = useMemo(() => {
    if (!opex_items_sets || opex_items_sets.length === 0) return [];
    const names: string[] = [];
    for (const set of opex_items_sets) {
      if (Array.isArray(set) && set.length > 0) {
        const nm = set[0]?.name as string | undefined;
        if (nm && !names.includes(nm)) names.push(nm);
      }
    }
    return names;
  }, [opex_items_sets]);

  // Year -> itemName -> value
  const opexItemMap: Record<number, Record<string, number>> = useMemo(() => {
    const map: Record<number, Record<string, number>> = {};
    if (!opex_items_sets) return map;
    for (const set of opex_items_sets) {
      for (const row of set) {
        const y = Number(row?.Year);
        const name = row?.name as string | undefined;
        const val = typeof row?.["OPEX (EUR)"] === "number" ? (row["OPEX (EUR)"] as number) : undefined;
        if (!name || !Number.isFinite(y) || typeof val !== "number") continue;
        map[y] = map[y] || {};
        map[y][name] = (map[y][name] || 0) + val;
      }
    }
    return map;
  }, [opex_items_sets]);


  // Row structure (with decimals + noTotal where applicable)
  const rowsDef: RowDef[] = useMemo(() => {
    const rows: RowDef[] = [
      // Production
      { section: "Production", key: "available_capacity_kwp",        label: t("project.financial_model.rows.available_capacity"),         unit: "kWp",           fmt: "number",  decimals: 1, noTotal: true },
      { section: "Production", key: "capacity_ratio_vs_initial",     label: t("project.financial_model.rows.capacity_ratio_vs_initial"),  unit: "%",             fmt: "percent",              noTotal: true },
      { section: "Production", key: "production_kwh",                label: t("project.financial_model.rows.production"),                 unit: "kWh",           fmt: "number",  bold: true },

      // Sales
      { section: "Sales",      key: "power_price",                   label: t("project.financial_model.rows.effective_power_price"),      unit: "EUR\u00A0ct.\u00A0/\u00A0kWh", fmt: "number", decimals: 1, noTotal: true },
      { section: "Sales",      key: "revenue_base",                  label: t("project.financial_model.rows.revenue_base"),               unit: "EUR",           fmt: "number" },
      { section: "Sales",      key: "storage_uplift",                label: t("project.financial_model.rows.storage_uplift"),             unit: "EUR",           fmt: "number" },
      { section: "Sales",      key: "neg_price_losses",              label: t("project.financial_model.rows.losses_negative_prices"),     unit: "EUR",           fmt: "number" },
      { section: "Sales",      key: "net_revenues",                  label: t("project.financial_model.rows.total_revenues"),             unit: "EUR",           fmt: "number", bold: true },

      // OpEx (items inserted before CapEx)
      // CapEx
      { section: "CapEx",      key: "capex_pv",                      label: t("project.financial_model.rows.capex_pv_system"),            unit: "EUR", fmt: "number" },
      { section: "CapEx",      key: "capex_storage",                 label: t("project.financial_model.rows.capex_storage_system"),       unit: "EUR", fmt: "number" },
      { section: "CapEx",      key: "capex_inverter",                label: t("project.financial_model.rows.capex_inverter_replacement"), unit: "EUR", fmt: "number" },
      { section: "CapEx",      key: "capex_broker",                  label: t("project.financial_model.rows.capex_broker_commission"),    unit: "EUR", fmt: "number" },
      { section: "CapEx",      key: "capex_total",                   label: t("project.financial_model.rows.capex_total_investment"),     unit: "EUR", fmt: "number", bold: true },

      // Financing
      { section: "Financing",  key: "new_debt",                      label: t("project.financial_model.rows.new_debt"),                   unit: "EUR", fmt: "number" },
      { section: "Financing",  key: "outstanding_debt_end",          label: t("project.financial_model.rows.outstanding_debt_end"),       unit: "EUR", fmt: "number" },
      { section: "Financing",  key: "interest_payment",              label: t("project.financial_model.rows.interest"),                   unit: "EUR", fmt: "number" },
      { section: "Financing",  key: "principal_payment",             label: t("project.financial_model.rows.principal"),                  unit: "EUR", fmt: "number" },
      { section: "Financing",  key: "structuring_costs",             label: t("project.financial_model.rows.structuring_costs"),          unit: "EUR", fmt: "number" },
      { section: "Financing",  key: "debt_service",                  label: t("project.financial_model.rows.debt_service"),               unit: "EUR", fmt: "number", bold: true },

      // Depreciation
      { section: "Depreciation", key: "iab",                         label: t("project.financial_model.rows.iab"),                        unit: "EUR", fmt: "number"  },
      { section: "Depreciation", key: "special_dep",                 label: t("project.financial_model.rows.special_depreciation_5y"),    unit: "EUR", fmt: "number"  },
      { section: "Depreciation", key: "base_dep",                    label: t("project.financial_model.rows.base_depreciation_excl"),     unit: "EUR", fmt: "number"  },
      { section: "Depreciation", key: "total_dep",                   label: t("project.financial_model.rows.total_depreciation"),         unit: "EUR", fmt: "number", bold: true },

      // Tax
      { section: "Tax",        key: "tax_operating_profit",          label: t("project.financial_model.rows.operating_profit"),           unit: "EUR", fmt: "number" },
      { section: "Tax",        key: "tax_interest",                  label: t("project.financial_model.rows.interest"),                   unit: "EUR", fmt: "number" },
      { section: "Tax",        key: "tax_structuring_costs",         label: t("project.financial_model.rows.structuring_costs"),          unit: "EUR", fmt: "number" },
      { section: "Tax",        key: "tax_depreciation",              label: t("project.financial_model.rows.depreciation"),               unit: "EUR", fmt: "number" },
      { section: "Tax",        key: "taxable_profit",                label: t("project.financial_model.rows.taxable_profit"),             unit: "EUR", fmt: "number" },
      { section: "Tax",        key: "tax_total",                     label: t("project.financial_model.rows.tax"),                        unit: "EUR", fmt: "number", bold: true },

      // Cash flow statement (prefer backend values)
      { section: "Cash flow statement", key: "cf_rev",               label: t("project.financial_model.rows.cf_revenues"),                unit: "EUR",  fmt: "number" },
      { section: "Cash flow statement", key: "cf_opex",              label: t("project.financial_model.rows.cf_opex"),                    unit: "EUR",  fmt: "number" },
      { section: "Cash flow statement", key: "cf_operating_profit",  label: t("project.financial_model.rows.cf_operating_profit"),        unit: "EUR",  fmt: "number" },
      { section: "Cash flow statement", key: "cf_investment",        label: t("project.financial_model.rows.cf_investment"),              unit: "EUR",  fmt: "number" },
      { section: "Cash flow statement", key: "cf_unlevered_pre_tax", label: t("project.financial_model.rows.cf_unlevered_pre_tax"),       unit: "EUR",  fmt: "number", bold: true },
      { section: "Cash flow statement", key: "cf_debt_funding",      label: t("project.financial_model.rows.cf_debt_funding"),            unit: "EUR",  fmt: "number" },
      { section: "Cash flow statement", key: "cf_debt_service",      label: t("project.financial_model.rows.cf_debt_service"),            unit: "EUR",  fmt: "number" },
      { section: "Cash flow statement", key: "cf_levered_pre_tax",   label: t("project.financial_model.rows.cf_levered_pre_tax"),         unit: "EUR",  fmt: "number", bold: true },
      { section: "Cash flow statement", key: "cf_tax",               label: t("project.financial_model.rows.cf_tax"),                     unit: "EUR",  fmt: "number" },
      { section: "Cash flow statement", key: "cf_to_equity",         label: t("project.financial_model.rows.cf_to_equity_post_tax"),      unit: "EUR",  fmt: "number", bold: true },
    ];

    // Insert OpEx items before CapEx
    const capexIdx = rows.findIndex(r => r.section === "CapEx");
    const opexRows: RowDef[] = [];
    if (opexItemNames.length > 0) {
      for (const nm of opexItemNames) {
        opexRows.push({ section: "OpEx", key: `opex_item__${nm}`, label: nm, unit: "EUR", fmt: "number" });
      }
    }
    opexRows.push({ section: "OpEx", key: "opex_total", label: "Total OpEx", unit: "EUR", fmt: "number", bold: true });
    rows.splice(capexIdx, 0, ...opexRows);

    return rows;
  }, [opexItemNames]);

  // formatting helpers
  const fmtNumber = (v: number, decimals?: number) => {
    const n = Math.abs(v) < 1e-9 ? 0 : v; // squash -0.0 and tiny epsilons
    return n.toLocaleString("en-US", {
      minimumFractionDigits: typeof decimals === "number" ? decimals : 0,
      maximumFractionDigits: typeof decimals === "number" ? decimals : 0,
    });
  };

  const fmtCell = (v: any, f?: CellFmt, decimals?: number) => {
    if (v === null || v === undefined) return "—";
    if (f === "percent" && typeof v === "number") return formatPercent(v);
    if (typeof v === "number") return fmtNumber(v, decimals);
    return String(v);
  };

  // build year -> values map according to the row keys
  const byYear = useMemo(() => {
    const m: Record<number, Record<string, number | undefined>> = {};

    // Pull IAB (EUR) from backend Year 1 to display in Year 0
    const iabY1 = get(dep, 1, "IAB (EUR)");

    for (const y of years) {
      const rec: Record<string, number | undefined> = {};

      // Production
      rec.available_capacity_kwp    = y === 0 ? undefined : get(prod, y, "Total capacity (kWp)");
      rec.capacity_ratio_vs_initial = y === 0 ? undefined : get(prod, y, "Total capacity compared to initial capacity");
      rec.production_kwh            = y === 0 ? undefined : get(prod, y, "Annual production (kWh)");

      // Sales — power price strictly from backend tariff (no fallback)
      rec.power_price       = y === 0 ? undefined : get(tariff, y, "Tariff (EUR ct. / kWh)");
      rec.revenue_base      = y === 0 ? undefined : get(rev, y, "PV revenue base (EUR)");
      rec.storage_uplift    = y === 0 ? undefined : get(rev, y, "Storage uplift (EUR)");
      rec.neg_price_losses  = y === 0 ? undefined : get(rev, y, "Negative price reduction (EUR)");
      rec.net_revenues      = y === 0 ? undefined : get(rev, y, "Revenue (EUR)");

      // OpEx
      if (y !== 0 && opexItemNames.length > 0) {
        const rowMap = opexItemMap[y] || {};
        for (const nm of opexItemNames) {
          rec[`opex_item__${nm}`] = rowMap[nm];
        }
      }
      const providedOpexTotal = y === 0 ? undefined : (get(opex_total, y, "OPEX (EUR)") ?? get(opex_total, y, "Opex (EUR)"));
      const itemsSum = opexItemNames.reduce<number>((sum, nm) => sum + (rec[`opex_item__${nm}`] ?? 0), 0);
      rec.opex_total = typeof providedOpexTotal === "number" ? providedOpexTotal : (y !== 0 && opexItemNames.length ? itemsSum : undefined);

      // CapEx (component columns if provided)
      rec.capex_pv       = y === 0 ? undefined : get(inv, y, "PV system (EUR)");
      rec.capex_storage  = y === 0 ? undefined : get(inv, y, "Storage system (EUR)");
      rec.capex_inverter = y === 0 ? undefined : get(inv, y, "Inverter replacement (EUR)");
      rec.capex_broker   = y === 0 ? undefined : get(inv, y, "Broker commission (EUR)");
      rec.capex_total    = y === 0 ? undefined : get(inv, y, "Investment (EUR)");

      // Financing
      rec.new_debt             = y === 0 ? undefined : get(fin_debt, y, "Debt (EUR)");
      rec.outstanding_debt_end = y === 0 ? undefined : get(fin_remaining_debt, y, "Remaining Debt (EUR)");
      rec.interest_payment     = y === 0 ? undefined : get(fin_interest, y, "Interest (EUR)");
      rec.principal_payment    = y === 0 ? undefined : get(fin_redemption, y, "Redemption (EUR)");
      rec.structuring_costs    = y === 0 ? undefined : get(fin_structuring, y, "Structuring Costs (EUR)");
      rec.debt_service         = y === 0 ? undefined : get(fin_debt_service, y, "Debt Service (EUR)");

      // Depreciation
      // IAB (EUR) in Year 0 only: Year-1 Investment (EUR) × iabRate
      // Note: "Investment (EUR)" is negative (cash out), so IAB will be negative.
      const iabRate: number = p?.investment_deduction_amount_iab ?? 0;
      const invY1 = get(inv, 1, "Investment (EUR)");
      const iabY0 = (typeof invY1 === "number" && typeof iabRate === "number") ? invY1 * iabRate : undefined;

      rec.iab = y === 0 ? iabY0 : undefined;

      const special   = y === 0 ? undefined : get(dep, y, "Special Depreciation (EUR)");
      const totalDep  = y === 0 ? (typeof iabY0 === "number" ? iabY0 : undefined) : get(dep, y, "Depreciation (EUR)");
      rec.special_dep = special;
      rec.total_dep   = totalDep;

      // Base depreciation (excl. IAB & special) = total - special (years >=1) - iab (year 0 only)
      rec.base_dep = y === 0 ? undefined :
        typeof totalDep === "number" ? totalDep 
        - (typeof special === "number" ? special : 0)
        : undefined;


      // Tax
      const taxes = arr((results as any)?.taxes);
      rec.tax_operating_profit = (typeof rec.net_revenues === "number" && typeof rec.opex_total === "number")
        ? rec.net_revenues + rec.opex_total
        : undefined;
      rec.tax_interest     = rec.interest_payment;   // usually negative
      rec.tax_structuring_costs = rec.structuring_costs;
      rec.tax_depreciation = rec.total_dep;          // usually negative
      rec.taxable_profit =
        y === 0
          ? iabY0       // show IAB in Year 0
          : (taxes ? get(taxes, y, "Taxable Profit (EUR)") : undefined);
      rec.tax_total =
        taxes
          ? (y === 0
              ? get(taxes, 1, "Taxes Cash (EUR)")          // Use first entry from backend (Year 1) for Year 0 display
              : get(taxes, y, "Taxes Accrual (EUR)"))      // Other years: accrual tax
          : undefined;

      // Cash flow statement
      rec.cf_rev              = rec.net_revenues;
      rec.cf_opex             = rec.opex_total;
      rec.cf_operating_profit = rec.tax_operating_profit;
      rec.cf_investment       = rec.capex_total;
      rec.cf_unlevered_pre_tax = y === 0 ? undefined : get(cashflow, y, "Cash Flow (unlevered, pre-tax) (EUR)");
      rec.cf_debt_funding      = y === 0 ? undefined : get(fin_net_debt, y, "Net Debt (EUR)");
      rec.cf_debt_service      = rec.debt_service;
      rec.cf_levered_pre_tax   = y === 0 ? undefined : get(cashflow, y, "Cash Flow (levered, pre-tax) (EUR)");
      rec.cf_tax               = taxes ? get(taxes, y, "Taxes Cash (EUR)") : undefined;
      rec.cf_to_equity         = y === 0 ? undefined : get(cashflow, y, "Cash Flow (EUR)");

      m[y] = rec;
    }
    return m;
  }, [
    years, prod, rev, inv, dep, tariff, cashflow,
    fin_interest, fin_redemption, fin_structuring, fin_debt_service, fin_remaining_debt, fin_debt,
    opex_total, opexItemNames, opexItemMap, results
  ]);

  // totals: sum for numbers, average for percents; allow per-row suppression; exclude Year 0 (except IAB)
  const totalsByRowKey: Record<string, number | undefined> = useMemo(() => {
    const t: Record<string, number | undefined> = {};
    for (const row of rowsDef) {
      if (row.noTotal) { t[row.key] = undefined; continue; }
      const selectedYears = years; // include Year 0 for all rows // ALTERNATIVE: const selectedYears = row.key === "iab" ? years : years.filter((y) => y !== 0);
      const values = selectedYears
        .map((y) => byYear[y]?.[row.key])
        .filter((x): x is number => typeof x === "number");
      if (values.length === 0) t[row.key] = undefined;
      else if (row.fmt === "percent") t[row.key] = values.reduce((a, b) => a + b, 0) / values.length;
      else t[row.key] = values.reduce((a, b) => a + b, 0);
    }
    return t;
  }, [rowsDef, years, byYear]);

  // widen Metric column; avoid wrapping
  const NAME_COL_W = 380;
  const stickyNameStyle: React.CSSProperties = { left: 0, width: NAME_COL_W, whiteSpace: "nowrap" };


  if (loading) return <div className="p-4 text-xs">{t("common.loading")}</div>;
  if (error)   return <div className="p-4 text-xs text-elani-orange">{t("common.error_loading_data")}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-sm font-bold text-gray-800 whitespace-nowrap">{t("project.financial_model.title")}</h1>
      </div>

      <div className="overflow-auto rounded border">
        <table className="min-w-[1100px] w-full text-[11px]">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th
                className="px-2 py-2 border-r text-left sticky z-20 bg-gray-50 whitespace-nowrap"
                style={stickyNameStyle}
              >
                {t("project.financial_model.metric")}
              </th>
              <th className="px-2 py-2 border-r text-left whitespace-nowrap">{t("project.financial_model.unit")}</th>
              {years.map((y) => (
                <th key={y} className="px-2 py-2 border-r text-right">{y}</th>
              ))}
              <th className="px-2 py-2 text-right">{t("project.financial_model.total")}</th>
            </tr>
          </thead>
          <tbody>
            {/* sections + rows */}
            {(() => {
              const rows: React.ReactNode[] = [];
              let currentSection = "";
              for (const row of rowsDef) {
                if (row.section !== currentSection) {
                  currentSection = row.section;
                  rows.push(
                    <tr key={`sec-${currentSection}`} className="bg-slate-100 border-t">
                      <td
                        className="px-2 py-1 border-r sticky left-0 z-10 font-semibold whitespace-nowrap"
                        style={{ ...stickyNameStyle }}
                        colSpan={2}
                      >
                        {SECTION_LABELS[currentSection] ?? currentSection}
                      </td>
                      {[...years, "TOTAL"].map((k, i) => (
                        <td key={String(k) + i} className="px-2 py-1 border-r"></td>
                      ))}
                    </tr>
                  );
                }
                rows.push(
                  <tr key={row.key} className="border-t">
                    <td
                      className={`px-2 py-1 border-r sticky left-0 z-10 bg-white whitespace-nowrap ${row.bold ? "font-semibold" : ""}`}
                      style={stickyNameStyle}
                    >
                      {row.label}
                    </td>
                    <td className={`px-2 py-1 border-r text-gray-600 whitespace-nowrap ${row.bold ? "font-semibold" : ""}`}>{row.unit ?? "—"}</td>
                    {years.map((y) => (
                      <td key={`${row.key}-${y}`} className="px-2 py-1 border-r text-right">
                        <span className={row.bold ? "font-semibold" : ""}>
                          {fmtCell((byYear[y] ?? {})[row.key], row.fmt, row.decimals)}
                        </span>
                      </td>
                    ))}
                    <td className="px-2 py-1 text-right">
                      <span className={row.bold ? "font-semibold" : ""}>
                        {row.noTotal ? "—" : fmtCell(totalsByRowKey[row.key], row.fmt)}
                      </span>
                    </td>
                  </tr>
                );
              }
              return rows;
            })()}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[10px] text-gray-600">
        {t("project.financial_model.tax_note")}
      </p>
    </div>
  );
}